/**
 * Componente: PurchaseFilters
 * 
 * Panel de filtros para el historial de compras que incluye:
 * - Rango de fechas (desde/hasta)
 * - Estado de compra (completed, pending, failed, refunded)
 * - Plan (basic, intermediate, premium)
 * - Búsqueda por número de orden o descripción
 * - Botón para limpiar todos los filtros
 * 
 * @param {Object} filters - Estado actual de los filtros
 * @param {Function} onFilterChange - Callback cuando cambia un filtro
 * @param {Function} onClearFilters - Callback para limpiar filtros
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Card, Grid, MenuItem } from '@mui/material';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDInput from 'components/MDInput';
import MDButton from 'components/MDButton';
import Icon from '@mui/material/Icon';
import { PURCHASE_STATUS, PURCHASE_TYPE, STATUS_CONFIG, TYPE_CONFIG } from 'services/purchaseService';

function PurchaseFilters({ filters, onFilterChange, onClearFilters }) {
  const handleChange = (field) => (event) => {
    onFilterChange({
      ...filters,
      [field]: event.target.value,
    });
  };

  const handleClear = () => {
    onClearFilters();
  };

  // Verifica si hay algún filtro activo
  const hasActiveFilters = Object.values(filters).some(value => value !== '' && value !== null);

  return (
    <Card sx={{ p: 3, mb: 3 }}>
      <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <MDTypography variant="h6" fontWeight="medium">
          Filtros de Búsqueda
        </MDTypography>
        {hasActiveFilters && (
          <MDButton
            variant="outlined"
            color="secondary"
            size="small"
            onClick={handleClear}
            startIcon={<Icon>clear</Icon>}
          >
            Limpiar Filtros
          </MDButton>
        )}
      </MDBox>

      <Grid container spacing={2}>
        {/* Búsqueda rápida */}
        <Grid item xs={12} md={6}>
          <MDInput
            label="Buscar por N° de orden o descripción"
            placeholder="Ej: ORD-2025..."
            fullWidth
            value={filters.search || ''}
            onChange={handleChange('search')}
            InputProps={{
              startAdornment: (
                <Icon fontSize="small" sx={{ mr: 1 }}>
                  search
                </Icon>
              ),
            }}
          />
        </Grid>

        {/* Estado */}
        <Grid item xs={12} sm={6} md={3}>
          <MDInput
            select
            label="Estado"
            fullWidth
            value={filters.status || ''}
            onChange={handleChange('status')}
          >
            <MenuItem value="">Todos los estados</MenuItem>
            <MenuItem value="pendiente">
              <MDBox display="flex" alignItems="center" gap={1}>
                <Icon fontSize="small" color="warning">pending</Icon>
                Pendiente
              </MDBox>
            </MenuItem>
            <MenuItem value="aprobado">
              <MDBox display="flex" alignItems="center" gap={1}>
                <Icon fontSize="small" color="success">check_circle</Icon>
                Aprobado
              </MDBox>
            </MenuItem>
            <MenuItem value="rechazado">
              <MDBox display="flex" alignItems="center" gap={1}>
                <Icon fontSize="small" color="error">cancel</Icon>
                Rechazado
              </MDBox>
            </MenuItem>
            <MenuItem value="devuelto">
              <MDBox display="flex" alignItems="center" gap={1}>
                <Icon fontSize="small" color="info">replay</Icon>
                Devuelto
              </MDBox>
            </MenuItem>
          </MDInput>
        </Grid>

        {/* Tipo de compra (Plan) */}
        <Grid item xs={12} sm={6} md={3}>
          <MDInput
            select
            label="Plan"
            fullWidth
            value={filters.type || ''}
            onChange={handleChange('type')}
          >
            <MenuItem value="">Todos los planes</MenuItem>
            {Object.entries(TYPE_CONFIG).map(([key, config]) => (
              <MenuItem key={key} value={key}>
                <MDBox display="flex" alignItems="center" gap={1}>
                  <Icon fontSize="small">{config.icon}</Icon>
                  {config.label}
                </MDBox>
              </MenuItem>
            ))}
          </MDInput>
        </Grid>

        {/* Fecha desde */}
        <Grid item xs={12} sm={6} md={3}>
          <MDInput
            type="date"
            label="Fecha Desde"
            fullWidth
            value={filters.dateFrom || ''}
            onChange={handleChange('dateFrom')}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        {/* Fecha hasta */}
        <Grid item xs={12} sm={6} md={3}>
          <MDInput
            type="date"
            label="Fecha Hasta"
            fullWidth
            value={filters.dateTo || ''}
            onChange={handleChange('dateTo')}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
      </Grid>

      {/* Indicador de filtros activos */}
      {hasActiveFilters && (
        <MDBox mt={2}>
          <MDTypography variant="caption" color="text" fontStyle="italic">
            {Object.entries(filters).filter(([_, value]) => value).length} filtro(s) activo(s)
          </MDTypography>
        </MDBox>
      )}
    </Card>
  );
}

PurchaseFilters.propTypes = {
  filters: PropTypes.shape({
    search: PropTypes.string,
    status: PropTypes.string,
    type: PropTypes.string,
    dateFrom: PropTypes.string,
    dateTo: PropTypes.string,
  }).isRequired,
  onFilterChange: PropTypes.func.isRequired,
  onClearFilters: PropTypes.func.isRequired,
};

export default PurchaseFilters;
